import { Injectable } from '@angular/core';

const COOKIE_CONSENT_KEY = 'cookie-consent-1';
const COOKIE_DOMAIN = 'vinnova.se';
const COOKIE_MAX_AGE_DAYS = 365;
const VALID_ENVIRONMENTS = ['local', 'utv', 'sys', 'acc'] as const;

type Environment = (typeof VALID_ENVIRONMENTS)[number] | 'prod';

export enum ConsentTypes {
  Required = 'required',
  Analytics = 'analytics',
  Blacklist = 'blacklist',
}
const blacklistUrlSegments = ['effekta'];

@Injectable({
  providedIn: 'root',
})
export class CookieConsentService {
  hasAnalyticsConsent(): boolean {
    const consent = this.getCookie(COOKIE_CONSENT_KEY);
    return consent === ConsentTypes.Analytics;
  }

  hasAnyConsent(): boolean {
    return !!this.getCookie(COOKIE_CONSENT_KEY);
  }

  setConsent(consent: ConsentTypes): void {
    const environment = this.getEnvironment();
    const maxAge = COOKIE_MAX_AGE_DAYS * 24 * 60 * 60;
    const domainPart =
      environment === 'prod'
        ? `;domain=${COOKIE_DOMAIN}`
        : `;domain=${environment}.${COOKIE_DOMAIN}`;
    document.cookie = `${COOKIE_CONSENT_KEY}=${encodeURIComponent(consent)}${domainPart};path=/;max-age=${maxAge};secure;samesite=lax`;
  }

  private getCookie(name: string): ConsentTypes | undefined {
    if (
      blacklistUrlSegments.some((segment) => document.URL.includes(segment))
    ) {
      return ConsentTypes.Blacklist;
    }

    const match = document.cookie.match(
      new RegExp(String.raw`(?:^|;\s*)${name}=([^;]*)`),
    );
    return match ? (decodeURIComponent(match[1]) as ConsentTypes) : undefined;
  }

  private getEnvironment(): Environment {
    const hostname = globalThis.location.hostname;
    if (hostname === 'local.vinnova.se') {
      return 'local';
    }
    const match = hostname.match(/\.(\w+)\.vinnova\.se$/);
    const env = match?.[1];
    if (
      env &&
      VALID_ENVIRONMENTS.includes(env as (typeof VALID_ENVIRONMENTS)[number])
    ) {
      return env as (typeof VALID_ENVIRONMENTS)[number];
    }
    return 'prod';
  }
}
